/* eslint-disable */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import * as url from "helper/UrlHelper";

const getStatusBadge = (completedpercent) => {
  switch (completedpercent) {
    case "0": return { color: "bg-secondary", label: "Pending" };
    case "1": return { color: "bg-primary",   label: "In Progress" };
    case "3": return { color: "bg-success",   label: "Completed" };
    case "4": return { color: "bg-danger",    label: "Failed" };
    default:  return { color: "bg-secondary", label: "Pending" };
  }
};

// ─── Payload preview builder ──────────────────────────────────────────────────
// Uses the stored payload_template when available (new format).
// Falls back to a default structure for older records.
const buildPayloadPreview = (automationConfig, isReactive, apiBaseUrl) => {
  if (!automationConfig) return null;

  // New format: payload_template is the full payload object
  if (
    automationConfig?.payload_template &&
    typeof automationConfig.payload_template === "object" &&
    Object.keys(automationConfig.payload_template).length > 0
  ) {
    return automationConfig.payload_template;
  }

  // Legacy fallback
  if (isReactive) {
    return {
      callbackUrl: `${apiBaseUrl}/api/project/updatetask/{{taskid}}`,
      callbackmethod: "PUT",
      taskid: "{{taskid}}",
      tasktitle: "{{tasktitle}}",
      taskdescription: "{{taskdescription}}",
      taskpercent: "{{taskpercent}}",
      taskstatus: "{{taskstatus}}",
      email: "{{email}}",
      name: "{{name}}",
      phone: "{{phone}}",
    };
  }
  return { taskid: "{{taskid}}", tasktitle: "{{tasktitle}}", email: "{{email}}", name: "{{name}}", phone: "{{phone}}" };
};

// ─────────────────────────────────────────────────────────────────────────────
// AutomationChildTaskDetailPopup
// Receives the full child task object as a prop (no extra API call needed)
// ─────────────────────────────────────────────────────────────────────────────
const AutomationChildTaskDetailPopup = ({ task, isOpen, onClose }) => {
  const { t } = useTranslation();

  // ── Unified editor state ──
  const [webhookUrlEditor, setWebhookUrlEditor]     = useState("");
  const [webhookMethodEditor, setWebhookMethodEditor] = useState("POST");
  const [tokenEditor, setTokenEditor]               = useState("");
  const [payloadEditor, setPayloadEditor]           = useState("");
  const [payloadJsonError, setPayloadJsonError]     = useState("");

  // ── Trigger state ──
  const [triggering, setTriggering]     = useState(false);
  const [triggerResult, setTriggerResult] = useState(null); // { success, message }

  // ── Unified save state ──
  const [saving, setSaving]       = useState(false);
  const [saveResult, setSaveResult] = useState(null); // { success, message }

  // Sync local state when task changes
  React.useEffect(() => {
    if (!task) return;
    const a = task.automation || {};
    setWebhookUrlEditor(a.webhook_url || "");
    setWebhookMethodEditor(a.webhook_method || "POST");
    setTokenEditor(a.token || "");
    const template = a.payload_template;
    setPayloadEditor(
      template
        ? (typeof template === "object" ? JSON.stringify(template, null, 2) : template)
        : ""
    );
    setTriggerResult(null);
    setPayloadJsonError("");
    setSaveResult(null);
  }, [task?._id]);

  if (!isOpen || !task) return null;

  const isReactive = task.executiontype === "reactive";
  const automationConfig = task.automation || null;
  const statusBadge = getStatusBadge(task.completedpercent);
  const proactiveUrl = `${url.API_BASE_URL}/automationtaskschedule/proactive?email=${task.moderator?.email || "{user_email}"}`;

  const handlePayloadChange = (value) => {
    setPayloadEditor(value);
    if (value.trim()) {
      try { JSON.parse(value); setPayloadJsonError(""); }
      catch { setPayloadJsonError(t("Invalid JSON format")); }
    } else {
      setPayloadJsonError("");
    }
  };

  // ── Unified Save: sends all automation fields to childtask payload endpoint ──
  const handleSave = async () => {
    if (payloadJsonError || !task?._id) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${url.API_BASE_URL}/automationtaskschedule/childtask/${task._id}/payload?token=${token}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            webhook_url:      webhookUrlEditor.trim(),
            webhook_method:   webhookMethodEditor,
            token:            tokenEditor.trim(),
            payload_template: payloadEditor,
          }),
        }
      );
      const data = await res.json();
      setSaveResult({ success: data.status, message: data.message });
    } catch {
      setSaveResult({ success: false, message: t("Network error while saving") });
    } finally {
      setSaving(false);
    }
  };

  const handleTrigger = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${url.API_BASE_URL}/automationtaskschedule/childtask/${task._id}/trigger?token=${token}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = await res.json();
      setTriggerResult({ success: data.status, message: data.message });
    } catch {
      setTriggerResult({ success: false, message: "Network error while triggering" });
    } finally {
      setTriggering(false);
    }
  };

  return (
    <>
      <div
        className="offcanvas offcanvas-end bg-white border-0 shadow-lg show d-flex flex-column"
        style={{ visibility: "visible", width: "80vw", maxWidth: "960px" }}
        tabIndex="-1"
        aria-labelledby="offcanvasChildTaskDetailLabel"
      >
        {/* Header */}
        <div className="offcanvas-header bg-primary text-white p-4 border-bottom flex-shrink-0">
          <div className="d-flex align-items-center gap-3">
            <div className="p-2 rounded-circle d-flex align-items-center justify-content-center"
              style={{ background: "rgba(255,255,255,0.15)" }}>
              <span className="material-symbols-outlined text-white" style={{ fontSize: "22px" }}>
                subdirectory_arrow_right
              </span>
            </div>
            <div>
              <h5 className="offcanvas-title fw-bold text-white mb-1" id="offcanvasChildTaskDetailLabel">
                {task.title}
              </h5>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                  {isReactive ? t("Reactive") : t("Proactive")}
                </span>
                <span className={`badge ${statusBadge.color}`} style={{ fontSize: "11px" }}>
                  {t(statusBadge.label)}
                </span>
                {task.customid && (
                  <small className="text-white opacity-75">#{task.customid}</small>
                )}
              </div>
            </div>
          </div>
          <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close" />
        </div>

        {/* Body — scrollable */}
        <div className="offcanvas-body p-4 flex-grow-1" style={{ overflowY: "auto" }}>

          {/* ── 1. Task Info + Parent ── */}
          <div className="row g-4 mb-4">
            {/* Task Info */}
            <div className="col-xl-6">
              <div className="h-100 p-4 rounded-3 border border-primary border-opacity-25 shadow-sm bg-light">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>task_alt</span>
                  <h6 className="mb-0 fw-bold text-dark">{t("Task Info")}</h6>
                </div>
                <h5 className="fw-bold text-dark mb-2">{task.title}</h5>
                {task.description && (
                  <p className="text-muted mb-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>{task.description}</p>
                )}
                <div className="row g-2">
                  {task.customid && (
                    <div className="col-6">
                      <small className="text-muted d-flex align-items-center gap-1 mb-1">
                        <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>tag</span>
                        {t("Custom ID")}
                      </small>
                      <span className="badge bg-light text-dark border fw-semibold">{task.customid}</span>
                    </div>
                  )}
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>flag</span>
                      {t("Status")}
                    </small>
                    <span className={`badge ${statusBadge.color}`}>{t(statusBadge.label)}</span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>calendar_today</span>
                      {t("Schedule Date")}
                    </small>
                    <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                      {task.startdate || "—"}
                    </span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>schedule</span>
                      {t("Schedule Time")}
                    </small>
                    <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                      {task.starttime || "—"}
                    </span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>bolt</span>
                      {t("Execution Type")}
                    </small>
                    <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                      {isReactive ? t("Reactive") : t("Proactive")}
                    </span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>event</span>
                      {t("Created")}
                    </small>
                    <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                      {new Date(task.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Lead + Parent Task */}
            <div className="col-xl-6 d-flex flex-column gap-3">
              {/* Lead */}
              {task.leadid && (
                <div className="p-4 rounded-3 border border-success border-opacity-25 shadow-sm bg-light">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="material-symbols-outlined text-success" style={{ fontSize: "20px" }}>person</span>
                    <h6 className="mb-0 fw-bold text-dark">{t("Lead")}</h6>
                  </div>
                  <div className="fw-semibold text-dark mb-1">
                    {task.leadid.name} {task.leadid.surname}
                  </div>
                  {task.leadid.email && (
                    <div className="text-muted d-flex align-items-center gap-1 mb-1" style={{ fontSize: "13px" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>mail</span>
                      {task.leadid.email}
                    </div>
                  )}
                  {task.leadid.phone && (
                    <div className="text-muted d-flex align-items-center gap-1 mb-1" style={{ fontSize: "13px" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>call</span>
                      {task.leadid.phone}
                    </div>
                  )}
                  {task.leadid.company && (
                    <div className="text-secondary d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>business</span>
                      {task.leadid.company}
                    </div>
                  )}
                </div>
              )}

              {/* Parent Task */}
              {task.previoustask && (
                <div className="p-4 rounded-3 border border-warning border-opacity-25 shadow-sm bg-light">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-warning" style={{ fontSize: "20px" }}>account_tree</span>
                    <h6 className="mb-0 fw-bold text-dark">{t("Parent Task")}</h6>
                    <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: "10px" }}>{t("Parent")}</span>
                  </div>
                  <span className="fw-semibold text-dark" style={{ fontSize: "14px" }}>
                    {task.previoustask.title || task.previoustask}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── 2. Webhook / Payload Config ── */}
          <div className="border rounded-3 overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-bottom d-flex align-items-center gap-2"
              style={{ background: isReactive ? "rgba(13,202,240,0.08)" : "rgba(255,193,7,0.08)" }}>
              <span className="material-symbols-outlined text-secondary" style={{ fontSize: "20px" }}>settings_ethernet</span>
              <h6 className="mb-0 fw-semibold text-dark">
                {isReactive ? t("Webhook Config") : t("Payload Config")}
              </h6>
              <small className="text-muted">
                {isReactive ? `— ${t("payload our server sends to n8n")}` : `— ${t("data our API returns to n8n's CRON")}`}
              </small>
              <span className={`badge ms-auto ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`} style={{ fontSize: "10px" }}>
                {isReactive ? (webhookMethodEditor || "POST") : "GET"}
              </span>
            </div>

            <div className="row g-0">
              {/* Left: editable config fields */}
              <div className="col-xl-6 p-4 border-end">
                {isReactive ? (
                  <>
                    {/* Webhook URL */}
                    <div className="mb-3">
                      <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                        <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>link</span>
                        {t("Webhook URL")}
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={webhookUrlEditor}
                        onChange={(e) => setWebhookUrlEditor(e.target.value)}
                        placeholder="https://your-n8n-instance/webhook/..."
                        style={{ fontSize: "12px" }}
                      />
                    </div>

                    {/* Method */}
                    <div className="mb-3">
                      <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                        <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>http</span>
                        {t("Method")}
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={webhookMethodEditor}
                        onChange={(e) => setWebhookMethodEditor(e.target.value)}
                        style={{ fontSize: "12px", maxWidth: "140px" }}
                      >
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="PATCH">PATCH</option>
                        <option value="GET">GET</option>
                      </select>
                    </div>

                    {/* Token */}
                    <div className="mb-3">
                      <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                        <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>key</span>
                        {t("Token")}
                        <span className="badge bg-secondary ms-1" style={{ fontSize: "9px" }}>{t("n8n auth")}</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm font-monospace"
                        value={tokenEditor}
                        onChange={(e) => setTokenEditor(e.target.value)}
                        placeholder={t("Leave blank if not required")}
                        style={{ fontSize: "11px" }}
                      />
                      <small className="text-muted">
                        {t("Sent as")} <code>Authorization: Bearer &lt;token&gt;</code> {t("so n8n can verify the request.")}
                      </small>
                    </div>

                    {/* n8n Execution ID (read-only) */}
                    {automationConfig?.n8n_execution_id && (
                      <div className="mt-3">
                        <label className="form-label fs-sm fw-semibold text-dark mb-1">{t("n8n Execution ID")}</label>
                        <code className="d-block p-2 bg-light rounded border" style={{ fontSize: "11px", color: "#555" }}>
                          {automationConfig.n8n_execution_id}
                        </code>
                      </div>
                    )}
                  </>
                ) : (
                  /* Proactive: show polling URL */
                  <div className="p-3 rounded-2 border mb-3" style={{ background: "#fff8e1" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="material-symbols-outlined text-warning" style={{ fontSize: "18px" }}>api</span>
                      <p className="mb-0 fw-semibold text-dark" style={{ fontSize: "13px" }}>
                        {t("n8n CRON polls this endpoint to get pending tasks:")}
                      </p>
                      <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: "10px" }}>GET</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <code className="flex-grow-1 p-2 rounded-2 border"
                        style={{ background: "#1e1e2e", color: "#cdd6f4", fontSize: "12px", wordBreak: "break-all", display: "block", lineHeight: "1.8" }}>
                        {proactiveUrl}
                      </code>
                      <button type="button" className="btn btn-sm btn-outline-secondary flex-shrink-0"
                        title={t("Copy URL")} onClick={() => navigator.clipboard?.writeText(proactiveUrl)}>
                        <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>content_copy</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: editable payload */}
              <div className="col-xl-6 p-4 d-flex flex-column" style={{ background: "#fafafa" }}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: "16px" }}>edit_note</span>
                  <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>{t("Payload")}</span>
                  <span className={`badge ms-1 ${isReactive ? "bg-info text-dark" : "bg-primary"}`} style={{ fontSize: "10px" }}>
                    {isReactive ? (webhookMethodEditor || "POST") : "GET"}
                  </span>
                </div>

                <textarea
                  className={`form-control font-monospace flex-grow-1 ${payloadJsonError ? "is-invalid" : ""}`}
                  rows="12"
                  value={payloadEditor}
                  onChange={(e) => handlePayloadChange(e.target.value)}
                  placeholder={t("No payload configured")}
                  style={{ fontSize: "11.5px", lineHeight: "1.6", resize: "none", minHeight: "240px" }}
                />
                {payloadJsonError && <div className="invalid-feedback d-block">{payloadJsonError}</div>}

                <small className="text-muted d-block mt-2">
                  <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: "13px" }}>info</span>
                  {t("Placeholders replaced at trigger time:")}
                  <ul className="mb-0 mt-1 ps-3" style={{ listStyle: "disc" }}>
                    <li><code>{"{{taskid}}"}</code> — {t("unique task ID")}</li>
                    <li><code>{"{{tasktitle}}"}</code> — {t("task title")}</li>
                    <li><code>{"{{taskdescription}}"}</code> — {t("task description")}</li>
                    <li><code>{"{{taskpercent}}"}</code> — {t("0–100 (completion %)")}</li>
                    <li><code>{"{{taskstatus}}"}</code> — {t("1=inprogress, 2=testing, 3=completed")}</li>
                    <li><code>{"{{email}}"}</code> — {t("lead email")}</li>
                    <li><code>{"{{name}}"}</code> — {t("lead full name")}</li>
                    <li><code>{"{{phone}}"}</code> — {t("lead phone")}</li>
                  </ul>
                </small>
              </div>
            </div>
          </div>

        </div>

        {/* ── Fixed Footer ── */}
        <div className="border-top p-3 bg-white flex-shrink-0 d-flex align-items-center gap-3">
          {/* Trigger Webhook */}
          <button
            type="button"
            className="btn btn-success d-flex align-items-center gap-2"
            disabled={triggering || (isReactive && !webhookUrlEditor.trim())}
            onClick={handleTrigger}
          >
            {triggering
              ? <><span className="spinner-border spinner-border-sm" /> {t("Triggering…")}</>
              : <><span className="material-symbols-outlined" style={{ fontSize: "18px" }}>play_circle</span> {t("Trigger Webhook")}</>}
          </button>

          {/* Trigger result feedback */}
          {triggerResult && (
            <small className={`d-flex align-items-center gap-1 ${triggerResult.success ? "text-success" : "text-danger"}`}>
              <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>
                {triggerResult.success ? "check_circle" : "error"}
              </span>
              {triggerResult.message}
            </small>
          )}

          {/* Save (unified) */}
          <button
            type="button"
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 ms-auto"
            disabled={saving || !!payloadJsonError}
            onClick={handleSave}
          >
            {saving
              ? <><span className="spinner-border spinner-border-sm" /> {t("Saving…")}</>
              : <><span className="material-symbols-outlined" style={{ fontSize: "16px" }}>save</span> {t("Save")}</>}
          </button>

          {/* Save result feedback */}
          {saveResult && (
            <small className={`d-flex align-items-center gap-1 ${saveResult.success ? "text-success" : "text-danger"}`}>
              <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>
                {saveResult.success ? "check_circle" : "error"}
              </span>
              {saveResult.message}
            </small>
          )}
        </div>
      </div>

      {/* Backdrop */}
      <div className="offcanvas-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default AutomationChildTaskDetailPopup;
